/**
 * Acta de recepción: la pantalla de un remito o factura que un emisor informó
 * a ANMAT hacia este GLN.
 *
 *   - Marcar lo recibido: por escaneo (modal) o con el tilde de cada fila.
 *   - Confirmar recibidas: job asíncrono en riga (`traza_job` tipo `ingreso`);
 *     mientras corre, `TrazaProgreso` y el acta se refrescan solos.
 *   - Cerrar: lo sin marcar queda como no recibida con motivo.
 *   - Reabrir una serie no recibida que llegó después.
 *   - Recuperar: reconcilia con ANMAT si el job falló o quedó colgado.
 *   - Comprobante (PDF) del acta.
 */

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileText,
  Loader2,
  Lock,
  RefreshCw,
  RotateCcw,
  ScanLine,
  Send,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TrazaProgreso } from '@/components/TrazaProgreso';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/notify';
import { fmtDate, fmtDateTime } from '@/lib/format';
import { abrirActaRecepcionPdf } from '@/lib/acta-recepcion-pdf';
import { useSessionStore } from '@/stores/session';
import {
  useActaRecepcion,
  useCerrarActaMutation,
  useConfirmarActaMutation,
  useDesmarcarSerieMutation,
  useMarcarSerieMutation,
  useQuitarSobranteMutation,
  useReabrirSerieMutation,
  useRecuperarActaMutation,
  useTrazaJobByReferencia,
} from '@/api/queries';
import type { ActaRecepcion, SerieActa } from '@/api/contracts/recepcion-acta';
import {
  ESTADO_ACTA_LABEL,
  ESTADO_SERIE_ACTA_LABEL,
  ESTADOS_SERIE_ACTA,
  type EstadoSerieActa,
} from '@/domain/types';
import {
  BADGE_ESTADO_ACTA,
  BADGE_ESTADO_SERIE,
  estaVencida,
  filtrarSeries,
  labelEstadoSerie,
  opcionesDeFiltro,
} from './acta-helpers';
import { EscaneoActaModal } from './EscaneoActaModal';
import { CerrarActaDialog } from './CerrarActaDialog';

interface Props {
  actaId: number;
  onVolver: () => void;
}

/**
 * Un job activo se considera colgado pasado este tiempo desde que se creó, más
 * un margen por serie (riga confirma de a una). Misma regla que el back
 * (`jobColgado` en acta-confirmacion.service.js): no se mide desde la última
 * consulta porque el poller la renueva aunque riga no avance.
 */
const JOB_COLGADO_MS = 15 * 60 * 1000;
const JOB_COLGADO_MS_POR_SERIE = 10 * 1000;

export function ActaRecepcionView({ actaId, onVolver }: Props) {
  const { data: acta, isLoading, error, refetch, isFetching } = useActaRecepcion(actaId);

  if (isLoading || !acta) {
    return (
      <div className="space-y-4">
        <VolverButton onVolver={onVolver} />
        {error ? (
          <Alert variant="destructive">
            <AlertDescription className="text-xs">
              No se pudo cargar el acta: {error instanceof Error ? error.message : String(error)}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="flex items-center justify-center py-12 text-gray-600">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando acta…
          </div>
        )}
      </div>
    );
  }

  return (
    <ActaCargada
      acta={acta}
      onVolver={onVolver}
      onRefrescar={() => void refetch()}
      refrescando={isFetching}
    />
  );
}

function ActaCargada({
  acta,
  onVolver,
  onRefrescar,
  refrescando,
}: {
  acta: ActaRecepcion;
  onVolver: () => void;
  onRefrescar: () => void;
  refrescando: boolean;
}) {
  const usuario = useSessionStore((s) => s.usuario);
  const { data: trazaLive } = useTrazaJobByReferencia(String(acta.id), 'ingreso');

  const [showEscaneo, setShowEscaneo] = useState(false);
  const [showCerrar, setShowCerrar] = useState(false);

  const marcar = useMarcarSerieMutation(acta.id);
  const desmarcar = useDesmarcarSerieMutation(acta.id);
  const quitarSobrante = useQuitarSobranteMutation(acta.id);
  const confirmar = useConfirmarActaMutation(acta.id);
  const cerrar = useCerrarActaMutation(acta.id);
  const reabrir = useReabrirSerieMutation(acta.id);
  const recuperar = useRecuperarActaMutation(acta.id);

  const abierta = acta.estado === 'pendiente';
  const confirmando = acta.estado === 'confirmando';
  const t = acta.totales;

  // "Recuperar" solo cuando tiene sentido: el job falló o no da señales de
  // vida, o hay series marcadas/rechazadas sin resolver en un acta abierta.
  const jobColgado = (() => {
    if (!confirmando) return false;
    if (!trazaLive) return true;
    if (trazaLive.status === 'failed') return true;
    if (trazaLive.status === 'pending' || trazaLive.status === 'running') {
      if (!trazaLive.createdAt) return false;
      const limite = JOB_COLGADO_MS + (trazaLive.totalItems ?? 0) * JOB_COLGADO_MS_POR_SERIE;
      return Date.now() - new Date(trazaLive.createdAt).getTime() > limite;
    }
    return false;
  })();
  // "Verificar con ANMAT" es una herramienta de administración (destraba un
  // acta cuyo job falló o quedó colgado): solo la ve el Administrador.
  const esAdmin = usuario?.rol === 'Administrador';
  const puedeRecuperar = esAdmin && (jobColgado || (abierta && (t.error > 0 || t.marcada > 0)));

  const onToggle = (s: SerieActa, checked: boolean) => {
    if (checked && s.estado === 'pendiente') {
      marcar.mutate({ via: 'manual', detailId: s.id }, { onError: (e) => toast.error(e.message) });
    } else if (!checked && s.estado === 'marcada') {
      desmarcar.mutate(s.id, { onError: (e) => toast.error(e.message) });
    }
  };

  // Everex no separa depósitos: lo confirmado entra siempre disponible, sin preguntar.
  const onConfirmar = () => {
    confirmar.mutate('a', {
      onSuccess: (r) => {
        toast.success(
          `Confirmación enviada · ${r.seriesEnviadas} ${r.seriesEnviadas === 1 ? 'serie' : 'series'}`,
        );
      },
      onError: (e) => toast.error(e.message),
    });
  };

  const onCerrar = (input: { motivo: string | null; detalle: string | null }) => {
    cerrar.mutate(
      {
        motivo:
          (input.motivo as
            | 'faltante'
            | 'danado'
            | 'enviado_por_error'
            | 'vencido'
            | 'otro'
            | null) ?? undefined,
        detalle: input.detalle ?? undefined,
      },
      {
        onSuccess: (r) => {
          setShowCerrar(false);
          toast.success(
            `Ingreso finalizado · ${ESTADO_ACTA_LABEL[r.estado]}${r.noRecibidas > 0 ? ` · ${r.noRecibidas} no recibida${r.noRecibidas === 1 ? '' : 's'}` : ''}`,
          );
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const onRecuperar = () => {
    recuperar.mutate(undefined, {
      onSuccess: (r) => {
        toast.success(
          `Verificado con ANMAT · ${r.confirmadas} confirmada${r.confirmadas === 1 ? '' : 's'}, ${r.anuladas} anulada${r.anuladas === 1 ? '' : 's'} por el emisor, ${r.sinNovedad} sin novedad`,
        );
      },
      onError: (e) => toast.error(e.message),
    });
  };

  const onPdf = () => {
    const ok = abrirActaRecepcionPdf({ acta, impresoPor: usuario?.nombre ?? null });
    if (!ok)
      toast.error('El navegador bloqueó la ventana del comprobante. Permití pop-ups y reintentá.');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <VolverButton onVolver={onVolver} />
        <button
          type="button"
          onClick={onRefrescar}
          disabled={refrescando}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 transition hover:border-everex-naranja hover:text-everex-azul disabled:opacity-50"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', refrescando && 'animate-spin')} /> Actualizar
        </button>
      </div>

      {/* Cabecera */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <span className="badge badge-purple">
              <b>RI</b> · Recepción ingreso
            </span>
            <CardTitle className="font-mono text-everex-azul">
              {acta.numeroRemito ?? acta.numeroFactura ?? `Acta #${acta.id}`}
            </CardTitle>
            <span className={cn('badge', BADGE_ESTADO_ACTA[acta.estado])}>
              {ESTADO_ACTA_LABEL[acta.estado]}
            </span>
            <span className="ml-auto text-xs text-gray-500">Acta #{acta.id}</span>
          </div>
          <p className="mt-1 text-xs text-gray-600">
            Emisor: <b>{acta.razonSocialOrigen ?? '—'}</b>
            {acta.glnOrigen ? <span className="font-mono"> ({acta.glnOrigen})</span> : null}
            {acta.numeroRemito && acta.numeroFactura ? (
              <>
                {' '}
                · Factura <span className="font-mono">{acta.numeroFactura}</span>
              </>
            ) : null}
            {' · '}Abierta {fmtDateTime(acta.createdAt)}
            {acta.cerradoAt ? ` · Cerrada ${fmtDateTime(acta.cerradoAt)}` : ''}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 text-xs">
            <Chip label="Declaradas" n={t.total} />
            <Chip
              label="Sin marcar"
              n={t.pendiente}
              tono={t.pendiente > 0 && abierta ? 'warn' : undefined}
            />
            <Chip label="Recibidas" n={t.marcada} tono={t.marcada > 0 ? 'info' : undefined} />
            <Chip label="Confirmadas" n={t.trazado} tono={t.trazado > 0 ? 'ok' : undefined} />
            <Chip label="Rechazadas" n={t.error} tono={t.error > 0 ? 'bad' : undefined} />
            <Chip
              label="No recibidas"
              n={t.noRecibida}
              tono={t.noRecibida > 0 ? 'warn' : undefined}
            />
            <Chip label="Anuladas por emisor" n={t.anulada} />
            <Chip
              label="Sobrantes"
              n={acta.sobrantes.length}
              tono={acta.sobrantes.length > 0 ? 'warn' : undefined}
            />
          </div>

          {confirmando && <TrazaProgreso traza={trazaLive ?? null} />}

          {acta.estado === 'anulada' && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                El emisor canceló en ANMAT todo lo que quedaba de este documento. No hay nada que
                recibir.
              </AlertDescription>
            </Alert>
          )}
          {acta.estado === 'trazado' && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-700" />
              <AlertDescription className="text-xs text-green-900">
                Recepción confirmada en ANMAT: {t.trazado} {t.trazado === 1 ? 'serie' : 'series'}.
                El stock ya fue registrado.
              </AlertDescription>
            </Alert>
          )}
          {jobColgado && (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-xs text-amber-900">
                La confirmación {trazaLive?.status === 'failed' ? 'falló' : 'no da señales de vida'}
                . Con <b>Verificar con ANMAT</b> se consulta qué pasó con cada serie y el acta se
                destraba.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {abierta && (
              <>
                <Button
                  onClick={() => setShowEscaneo(true)}
                  className="bg-everex-azul text-white hover:bg-everex-azul/90"
                >
                  <ScanLine className="mr-2 h-4 w-4" /> Escanear
                </Button>
                <Button
                  onClick={onConfirmar}
                  disabled={t.marcada === 0 || confirmar.isPending}
                  className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
                  title={
                    t.marcada === 0
                      ? 'Marcá al menos una serie como recibida'
                      : 'Confirma en ANMAT las series marcadas; entran al stock como disponibles'
                  }
                >
                  {confirmar.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  Confirmar recibidas ({t.marcada})
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCerrar(true)}
                  title="Finaliza el ingreso: lo sin marcar queda como no recibido"
                >
                  <Lock className="mr-2 h-4 w-4" /> Finalizar ingreso
                </Button>
              </>
            )}
            {puedeRecuperar && (
              <Button variant="outline" onClick={onRecuperar} disabled={recuperar.isPending}>
                {recuperar.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="mr-2 h-4 w-4" />
                )}
                Verificar con ANMAT
              </Button>
            )}
            <Button variant="outline" onClick={onPdf} className="ml-auto">
              <FileText className="mr-2 h-4 w-4" /> Comprobante
            </Button>
          </div>
        </CardContent>
      </Card>

      <TablaSeries
        acta={acta}
        abierta={abierta}
        ocupado={marcar.isPending || desmarcar.isPending}
        onToggle={onToggle}
        onReabrir={(s) =>
          reabrir.mutate(s.id, {
            onSuccess: () =>
              toast.success(`Serie ${s.numeroSerial ?? ''} reabierta: marcala y confirmala.`),
            onError: (e) => toast.error(e.message),
          })
        }
      />

      {acta.sobrantes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-everex-azul">
              Unidades no declaradas por el emisor{' '}
              <span className="text-gray-500">({acta.sobrantes.length})</span>
            </CardTitle>
            <p className="text-xs text-gray-600">
              Llegaron físicamente pero ANMAT no las lista para este documento. No se pueden
              confirmar ni entran al stock hasta que el emisor las informe.
            </p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                <tr>
                  <th className="px-2 py-2 text-left">GTIN</th>
                  <th className="px-2 py-2 text-left">Lote</th>
                  <th className="px-2 py-2 text-left">N° serie</th>
                  <th className="px-2 py-2 text-left">Vto.</th>
                  <th className="px-2 py-2 text-left">Origen</th>
                  <th className="px-2 py-2 text-left">Registrado</th>
                  <th className="px-2 py-2" aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {acta.sobrantes.map((x) => (
                  <tr key={x.id} className="border-t border-gray-100">
                    <td className="px-2 py-2 font-mono">{x.gtin ?? '—'}</td>
                    <td className="px-2 py-2 font-mono">{x.lote ?? '—'}</td>
                    <td className="px-2 py-2 font-mono font-semibold">{x.numeroSerial}</td>
                    <td className="px-2 py-2">{fmtDate(x.vencimiento)}</td>
                    <td className="px-2 py-2 text-gray-600">
                      {x.origen?.tipo === 'reingreso'
                        ? `Ya pasó por el stock${x.origen.despacho?.referencia ? ` · ${x.origen.despacho.referencia}` : ''}`
                        : 'Desconocida'}
                    </td>
                    <td className="px-2 py-2 text-gray-600">
                      {fmtDateTime(x.createdAt)} · {x.usuario ?? '—'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {abierta && (
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800"
                          title="Quitar el sobrante (registrado por error)"
                          onClick={() =>
                            quitarSobrante.mutate(x.id, { onError: (e) => toast.error(e.message) })
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <EscaneoActaModal open={showEscaneo} onOpenChange={setShowEscaneo} acta={acta} />
      <CerrarActaDialog
        open={showCerrar}
        onOpenChange={setShowCerrar}
        pendientes={t.pendiente}
        marcadas={t.marcada}
        errores={t.error}
        pending={cerrar.isPending}
        onCerrar={onCerrar}
      />
    </div>
  );
}

function VolverButton({ onVolver }: { onVolver: () => void }) {
  return (
    <button
      type="button"
      onClick={onVolver}
      className="inline-flex items-center gap-1.5 text-xs text-gray-600 transition hover:text-everex-azul"
    >
      <ArrowLeft className="h-3.5 w-3.5" /> Volver al listado
    </button>
  );
}

function Chip({
  label,
  n,
  tono,
}: {
  label: string;
  n: number;
  tono?: 'ok' | 'bad' | 'warn' | 'info';
}) {
  const clase =
    tono === 'ok'
      ? 'badge-green'
      : tono === 'bad'
        ? 'badge-red'
        : tono === 'warn'
          ? 'badge-yellow'
          : tono === 'info'
            ? 'badge-blue'
            : 'badge-gray';
  return (
    <span className={cn('badge font-normal', clase)}>
      {label} <b className="ml-1">{n}</b>
    </span>
  );
}

function TablaSeries({
  acta,
  abierta,
  ocupado,
  onToggle,
  onReabrir,
}: {
  acta: ActaRecepcion;
  abierta: boolean;
  ocupado: boolean;
  onToggle: (s: SerieActa, checked: boolean) => void;
  onReabrir: (s: SerieActa) => void;
}) {
  const [texto, setTexto] = useState('');
  const [gtin, setGtin] = useState('todos');
  const [lote, setLote] = useState('todos');
  const [estado, setEstado] = useState<EstadoSerieActa | 'todas'>('todas');

  const opciones = useMemo(() => opcionesDeFiltro(acta.series), [acta.series]);
  const filas = useMemo(
    () =>
      filtrarSeries(acta.series, {
        texto,
        gtin: gtin === 'todos' ? undefined : gtin,
        lote: lote === 'todos' ? undefined : lote,
        estado,
      }),
    [acta.series, texto, gtin, lote, estado],
  );
  const puedeReabrir = ['parcial', 'error', 'pendiente'].includes(acta.estado);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-end gap-2">
          <CardTitle className="mr-auto text-base text-everex-azul">
            Series declaradas por el emisor{' '}
            <span className="text-gray-500">
              ({filas.length}
              {filas.length !== acta.series.length ? ` de ${acta.series.length}` : ''})
            </span>
          </CardTitle>
          <Input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Serie, lote, GTIN o producto"
            className="w-[220px] font-mono"
          />
          <Select value={gtin} onValueChange={setGtin}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Producto" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los productos</SelectItem>
              {opciones.productos.map((p) => (
                <SelectItem key={p.gtin} value={p.gtin}>
                  {p.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={lote} onValueChange={setLote}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Lote" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los lotes</SelectItem>
              {opciones.lotes.map((l) => (
                <SelectItem key={l} value={l}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={estado} onValueChange={(v) => setEstado(v as EstadoSerieActa | 'todas')}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todos los estados</SelectItem>
              {ESTADOS_SERIE_ACTA.map((e) => (
                <SelectItem key={e} value={e}>
                  {ESTADO_SERIE_ACTA_LABEL[e]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
              <tr>
                <th className="px-2 py-2 text-left" title="Recibida">
                  Rec.
                </th>
                <th className="px-2 py-2 text-left">Producto</th>
                <th className="px-2 py-2 text-left">GTIN</th>
                <th className="px-2 py-2 text-left">Lote</th>
                <th className="px-2 py-2 text-left">N° serie</th>
                <th className="px-2 py-2 text-left">Vto.</th>
                <th className="px-2 py-2 text-left">Evento</th>
                <th className="px-2 py-2 text-left">Estado</th>
                <th className="px-2 py-2 text-left">Origen</th>
                <th className="px-2 py-2" aria-label="Acciones" />
              </tr>
            </thead>
            <tbody className="text-gray-800">
              {filas.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-2 py-8 text-center text-gray-500">
                    Ninguna serie coincide con el filtro.
                  </td>
                </tr>
              ) : (
                filas.map((s) => {
                  const marcable = abierta && (s.estado === 'pendiente' || s.estado === 'marcada');
                  const vencida = estaVencida(s.vencimiento);
                  return (
                    <tr
                      key={s.id}
                      className={cn(
                        'border-t border-gray-100',
                        s.estado === 'marcada' && 'bg-blue-50/40',
                        s.estado === 'error' && 'bg-red-50/40',
                      )}
                    >
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          aria-label={`Recibida ${s.numeroSerial ?? ''}`}
                          checked={s.estado === 'marcada' || s.estado === 'trazado'}
                          disabled={!marcable || ocupado}
                          onChange={(e) => onToggle(s, e.target.checked)}
                        />
                      </td>
                      <td className="px-2 py-2">{s.nombre ?? '—'}</td>
                      <td className="px-2 py-2 font-mono">{s.gtin ?? '—'}</td>
                      <td className="px-2 py-2 font-mono">{s.lote ?? '—'}</td>
                      <td className="px-2 py-2 font-mono font-semibold">{s.numeroSerial ?? '—'}</td>
                      <td
                        className={cn('px-2 py-2', vencida && 'text-red-700')}
                        title={vencida ? 'Vencida' : undefined}
                      >
                        {fmtDate(s.vencimiento)}
                      </td>
                      <td
                        className="px-2 py-2 text-[11px]"
                        title={s.descripcionEvento ?? undefined}
                      >
                        {s.descripcionEvento ?? '—'}
                      </td>
                      <td className="px-2 py-2">
                        <span className={cn('badge font-normal', BADGE_ESTADO_SERIE[s.estado])}>
                          {labelEstadoSerie(s)}
                        </span>
                        {s.estado === 'error' && s.errorMessage && (
                          <div className="mt-0.5 max-w-[260px] text-[10px] text-red-700">
                            {s.errorMessage}
                          </div>
                        )}
                        {s.estado === 'no_recibida' && s.noRecibidaDetalle && (
                          <div className="mt-0.5 max-w-[260px] text-[10px] text-gray-600">
                            {s.noRecibidaDetalle}
                          </div>
                        )}
                        {s.estado === 'trazado' && s.idTransacTraza && (
                          <div
                            className="mt-0.5 font-mono text-[10px] text-gray-500"
                            title="ID de la confirmación en ANMAT"
                          >
                            {s.idTransacTraza}
                          </div>
                        )}
                        {s.marcadaUsuario && (s.estado === 'marcada' || s.estado === 'trazado') && (
                          <div className="mt-0.5 text-[10px] text-gray-500">
                            {s.marcadaVia === 'escaneo' ? 'Escaneada' : 'Marcada'} por{' '}
                            {s.marcadaUsuario}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-[11px] text-gray-600">
                        {s.origen?.tipo === 'reingreso' ? (
                          <span
                            title={
                              s.origen.fEgreso
                                ? `Egresó ${fmtDateTime(s.origen.fEgreso)}`
                                : undefined
                            }
                          >
                            Devolución
                            {s.origen.despacho?.referencia ? (
                              <span className="block font-mono text-[10px]">
                                {s.origen.despacho.referencia}
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          <span title="Nunca pasó por el stock de este sistema">Ingreso nuevo</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {puedeReabrir && s.estado === 'no_recibida' && s.anmatIdEstado !== '2' && (
                          <button
                            type="button"
                            onClick={() => onReabrir(s)}
                            className="text-[11px] text-everex-azul underline-offset-2 hover:underline"
                            title="La unidad llegó después: vuelve a 'sin marcar' y el acta se reabre"
                          >
                            Llegó
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
